import {
  tablesDB,
  DATABASE_ID,
  ORDERS_TABLE_ID,
  STORE_TABLE_ID,
  USERS_TABLE_ID,
  DRIVERS_TABLE_ID,
  Query,
} from '../lib/appwrite';
import { listDeliveryRequests } from './adminDeliveryService';
import { filterByTimeRange, getBucketsForTimeRange, getDateKeyForBucket } from '../utils/timeRange';
import { currentRequestByOrderId } from '../utils/deliveryRequestUtils';

export async function listOrders(queries = []) {
  const res = await tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, queries);
  return res.rows || [];
}

export async function getOrder(orderId) {
  const res = await tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
    Query.equal('orderId', orderId),
  ]);
  const rows = res.rows || [];
  return rows[0] || null;
}

/**
 * Get order stats for a customer (order count, total spent).
 */
export async function getCustomerOrderStats(customerId) {
  if (!customerId) return { orderCount: 0, totalSpent: 0 };
  const res = await tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
    Query.equal('customerId', customerId),
    Query.limit(2000),
  ]);
  const rows = res.rows || [];
  const byOrder = {};
  rows.forEach((r) => {
    const key = r.orderId || r.$id;
    if (!byOrder[key]) byOrder[key] = 0;
    byOrder[key] += parseFloat(r.totalAmount) || 0;
  });
  const orderCount = Object.keys(byOrder).length;
  const totalSpent = Object.values(byOrder).reduce((s, v) => s + v, 0);
  return { orderCount, totalSpent };
}

export async function updateOrder(rowId, data) {
  return tablesDB.updateRow(DATABASE_ID, ORDERS_TABLE_ID, rowId, data);
}

/**
 * Get all orders with lookup data (stores, customers, drivers) and optional filters.
 * Returns grouped orders (one order can have multiple rows/items).
 * @param {Object} filters - { storeId, customerId, driverId, timeRange }
 */
export async function getOrdersForAdmin(filters = {}) {
  const { timeRange = 'month' } = filters;

  const [ordersRes, storesRes, requestsRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
      Query.orderDesc('orderDate'),
      Query.limit(5000),
    ]),
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(500)]),
    listDeliveryRequests(),
  ]);

  const allOrders = ordersRes.rows || [];
  const orders = timeRange ? filterByTimeRange(allOrders, timeRange) : allOrders;
  const stores = storesRes.rows || [];
  const requests = requestsRes || [];

  const storeMap = stores.reduce((acc, s) => {
    acc[s.$id] = s.storeName || s.$id;
    return acc;
  }, {});

  // A cancelled-then-rebooked delivery leaves both requests under the same
  // order; take the current one, not whichever came back last from the loop.
  const currentRequest = currentRequestByOrderId(requests);
  const orderIdToDriver = {};
  const orderIdToRequest = {};
  Object.entries(currentRequest).forEach(([orderId, r]) => {
    if (!r) return;
    if (r.driverId) {
      orderIdToDriver[orderId] = { driverId: r.driverId, driverName: r.driverName || r.driverId };
    }
    if (r.$id) {
      orderIdToRequest[orderId] = r.$id;
    }
  });

  let orderIdsByDriver = null;
  if (filters.driverId) {
    orderIdsByDriver = new Set(
      requests.filter((r) => r.driverId === filters.driverId).map((r) => r.orderId).filter(Boolean)
    );
  }

  const grouped = {};
  orders.forEach((o) => {
    if (filters.storeId && o.storeId !== filters.storeId) return;
    if (filters.customerId && o.customerId !== filters.customerId) return;
    if (orderIdsByDriver && !orderIdsByDriver.has(o.orderId)) return;

    const key = o.orderId || o.$id;
    if (!grouped[key]) {
      grouped[key] = {
        orderId: o.orderId || o.$id,
        customerId: o.customerId,
        storeId: o.storeId,
        storeName: storeMap[o.storeId] || o.storeId,
        orderDate: o.orderDate || o.$createdAt,
        status: o.status,
        trackingNumber: o.trackingNumber,
        shippingAddress: o.shippingAddress,
        paymentNumber: o.paymentNumber,
        deliveryFee: o.deliveryFee,
        deliveryMethod: o.deliveryMethod,
        deliveryRequestId: orderIdToRequest[key] || null,
        items: [],
        totalAmount: 0,
        driver: orderIdToDriver[key] || null,
        $id: o.$id,
        $createdAt: o.$createdAt,
      };
    }
    grouped[key].items.push({
      productName: o.productName,
      productId: o.productId,
      quantity: o.quantity,
      packSize: o.packSize,
      unit: o.unit,
      totalAmount: parseFloat(o.totalAmount) || 0,
    });
    grouped[key].totalAmount += parseFloat(o.totalAmount) || 0;
  });

  return Object.values(grouped).sort((a, b) => {
    const da = new Date(a.orderDate || 0).getTime();
    const db = new Date(b.orderDate || 0).getTime();
    return db - da;
  });
}

/**
 * Get order analytics and filter options (stores, customers, drivers)
 * @param {Object} options - { timeRange: 'day'|'week'|'month'|'quarter'|'year' }
 */
export async function getOrderAnalyticsAndFilters(options = {}) {
  const { timeRange = 'month' } = options;

  const [ordersRes, storesRes, requestsRes, driversRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(500)]),
    listDeliveryRequests(),
    tablesDB.listRows(DATABASE_ID, DRIVERS_TABLE_ID, [Query.limit(500)]),
  ]);

  const allOrders = ordersRes.rows || [];
  const orders = timeRange ? filterByTimeRange(allOrders, timeRange) : allOrders;
  const stores = storesRes.rows || [];
  const requests = requestsRes || [];
  const drivers = driversRes.rows || [];

  const storeMap = stores.reduce((acc, s) => {
    acc[s.$id] = { name: s.storeName || s.$id, id: s.$id };
    return acc;
  }, {});

  const driverMap = drivers.reduce((acc, d) => {
    acc[d.$id] = d.fname || d.lname || d.driverName || d.$id;
    return acc;
  }, {});

  const customerIds = [...new Set(orders.map((o) => o.customerId).filter(Boolean))];
  const customerPromises = customerIds.slice(0, 200).map((id) =>
    tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, id).catch(() => ({ $id: id, fname: 'Unknown', lname: '', email: null }))
  );
  const customerRows = await Promise.all(customerPromises);
  const customerMap = {};
  const customerDetailMap = {};
  customerRows.forEach((u) => {
    customerMap[u.$id] = [u.fname, u.lname].filter(Boolean).join(' ') || u.email || u.$id;
    customerDetailMap[u.$id] = { email: u.email || null, phone: u.phoneNumber || u.phone || null };
  });

  const bucketConfig = getBucketsForTimeRange(timeRange);
  const byMonth = {};
  bucketConfig.forEach((b) => {
    byMonth[b.key] = { label: b.label, orders: 0, revenue: 0 };
  });

  const uniqueOrders = new Set(orders.map((o) => o.orderId));
  let totalRevenue = 0;
  const orderStatusSet = {};

  orders.forEach((o) => {
    totalRevenue += parseFloat(o.totalAmount) || 0;
    const status = (o.status || 'unknown').toLowerCase();
    if (!orderStatusSet[status]) orderStatusSet[status] = new Set();
    orderStatusSet[status].add(o.orderId);
  });

  const byStatus = Object.entries(orderStatusSet).map(([status, ids]) => ({
    status,
    count: ids.size,
  }));

  const ordersByBucket = {};
  orders.forEach((o) => {
    const dt = o.orderDate || o.$createdAt || 0;
    const key = getDateKeyForBucket(dt, timeRange);
    if (key) {
      if (!ordersByBucket[key]) ordersByBucket[key] = new Set();
      ordersByBucket[key].add(o.orderId);
      if (!byMonth[key]) byMonth[key] = { label: key, orders: 0, revenue: 0 };
      byMonth[key].revenue += parseFloat(o.totalAmount) || 0;
    }
  });
  Object.keys(byMonth).forEach((k) => {
    byMonth[k].orders = ordersByBucket[k] ? ordersByBucket[k].size : 0;
  });

  const driversWithOrders = [...new Set(requests.map((r) => r.driverId).filter(Boolean))].map((id) => ({
    id,
    name: driverMap[id] || requests.find((r) => r.driverId === id)?.driverName || id,
  }));

  return {
    filters: {
      stores: stores.map((s) => ({ id: s.$id, name: s.storeName || s.$id })),
      customers: Object.entries(customerMap).map(([id, name]) => ({ id, name })),
      drivers: driversWithOrders,
    },
    analytics: {
      totalOrders: uniqueOrders.size,
      totalRevenue,
      byMonth: Object.entries(byMonth).map(([k, v]) => ({ key: k, ...v })),
      byStatus,
    },
    storeMap,
    customerMap,
    customerDetailMap,
    driverMap,
  };
}
